import React, { useState } from "react";
import axios from "axios";
import {
  Box,
  Container,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
} from "@mui/material";

/* ================= API CONFIG ================= */
const API_URL =
  "https://ritehomepros.com/marketing_leads_backend/enquiry/create/";
const BUSINESS_ID = "e3896a07-6db2-4e91-a48f-9a25a9d07e9d";

const ContactBookingSection = () => {
  /* ================= STATE ================= */
  const [form, setForm] = useState({
    fname: "",
    lname: "",
    email: "",
    mobile: "",
    service: "",
    message: "",
  });

  const [loading, setLoading] = useState(false);

  const [snack, setSnack] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  /* ================= VALIDATION ================= */
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const validateForm = () => {
    if (!form.service) return "Please select a service";
    if (!form.fname.trim()) return "First name is required";
    if (!emailRegex.test(form.email)) return "Enter a valid email";
    if (form.mobile.length !== 10)
      return "Enter a valid 10-digit phone number";
    return null;
  };

  /* ================= HANDLERS ================= */
  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 10);
    handleChange("mobile", value);
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) {
      setSnack({ open: true, message: error, severity: "error" });
      return;
    }

    try {
      setLoading(true);

      await axios.post(API_URL, {
        business: BUSINESS_ID,
        fname: form.fname,
        lname: form.lname || "Customer",
        mobile: form.mobile,
        email: form.email,
        source: "business_website",
        service: form.service,
        message: form.message,
      });

      setSnack({
        open: true,
        message: "Your message has been sent successfully!",
        severity: "success",
      });

      setForm({
        fname: "",
        lname: "",
        email: "",
        mobile: "",
        service: "",
        message: "",
      });
    } catch (err) {
      setSnack({
        open: true,
        message: "Something went wrong. Please try again.",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        py: { xs: 6, md: 10 },
        backgroundColor: "#f7f4ef",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="xl">
        <Grid container alignItems="center">
          {/* LEFT FLOAT IMAGE */}
          <Grid
            item
            xs={12}
            lg={3}
            sx={{ display: { xs: "none", lg: "block" } }}
          >
           
          </Grid>

          {/* FORM CARD */}
          <Grid item xs={12} lg={6}>
            <Box
              sx={{
                backgroundColor: "rgba(255,255,255,0.85)",
                backdropFilter: "blur(8px)",
                borderRadius: 4,
                p: { xs: 3, md: 4 },
                zIndex: 2,
              }}
            >
              {/* HEADER */}
              <Typography
                textAlign="center"
                sx={{
                  color: "#ff6d00",
                  fontWeight: 600,
                  mb: 1,
                }}
              >
                Get in touch
              </Typography>

              <Typography
                variant="h5"
                textAlign="center"
                fontWeight={700}
                mb={3}
              >
                Book Your Clean Today
              </Typography>

              {/* FORM */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="First Name*"
                    value={form.fname}
                    onChange={(e) =>
                      handleChange("fname", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Last Name"
                    value={form.lname}
                    onChange={(e) =>
                      handleChange("lname", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Your E-mail*"
                    value={form.email}
                    onChange={(e) =>
                      handleChange("email", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Phone Number*"
                    value={form.mobile}
                    onChange={handlePhoneChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    value={form.service}
                    onChange={(e) =>
                      handleChange("service", e.target.value)
                    }
                  >
                    <MenuItem value="">Type of service</MenuItem>
                    <MenuItem value="Water Extraction & Structural Drying">
                      Water Extraction & Structural Drying
                    </MenuItem>
                    <MenuItem value="Mold Prevention & Remediation">
                      Mold Prevention & Remediation
                    </MenuItem>
                    <MenuItem value="Sewage Cleanup (Black Water)">
                      Sewage Cleanup (Black Water)
                    </MenuItem>
                    <MenuItem value="Content Cleaning & Pack Out">
                     Content Cleaning & Pack Out
                    </MenuItem>
                    <MenuItem value="Drywall & Flooring Repair">
                     Drywall & Flooring Repair
                    </MenuItem>
                    <MenuItem value="Emergency Roof Tarping & Leak Response">
                     Emergency Roof Tarping & Leak Response
                    </MenuItem>
                  </TextField>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Type your message"
                    value={form.message}
                    onChange={(e) =>
                      handleChange("message", e.target.value)
                    }
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    disabled={loading}
                    sx={{
                      backgroundColor: "#ff6d00",
                      color: "#fff",
                      py: 1.4,
                      borderRadius: 3,
                      fontWeight: 600,
                      "&:hover": {
                        backgroundColor: "#e65f00",
                      },
                    }}
                  >
                    {loading ? (
                      <CircularProgress size={22} color="inherit" />
                    ) : (
                      "Send"
                    )}
                  </Button>
                </Grid>
              </Grid>

              {/* FOOT NOTE */}
              <Typography
                mt={2}
                fontSize={13}
                color="text.secondary"
              >
                <span style={{ color: "#ff6d00" }}>*</span> Required
                fields
              </Typography>
            </Box>
          </Grid>

          {/* RIGHT FLOAT IMAGE */}
      
        </Grid>
      </Container>

      {/* SNACKBAR */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactBookingSection;
